/**
 * PlanGate — wraps content that requires a minimum plan tier.
 * Shows a dimmed overlay with upgrade prompt for insufficient plans.
 * Clicking upgrade goes directly to Stripe checkout.
 */

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Plan } from '@dealcloser/shared'
import { Lock, Loader2, CheckCircle2 } from 'lucide-react'
import { useApi } from '@/hooks/use-api'

const PLAN_ORDER: Record<Plan, number> = { starter: 0, pro: 1, agency: 2 }

/**
 * Response of POST /api/billing/checkout. Users WITHOUT a subscription get a
 * Stripe Checkout `url`; users who already subscribe are upgraded in place
 * (`upgraded: true`) so we never create a second subscription.
 */
export type CheckoutResponse = {
  readonly url?: string
  readonly upgraded?: boolean
  readonly plan?: Plan
}

type PlanGateProps = {
  readonly requiredPlan: Plan
  readonly currentPlan: Plan
  readonly children: React.ReactNode
  /** When true, hides children entirely and shows only the upgrade CTA. */
  readonly replace?: boolean
}

export function PlanGate({ requiredPlan, currentPlan, children, replace }: PlanGateProps) {
  const hasAccess = PLAN_ORDER[currentPlan] >= PLAN_ORDER[requiredPlan]
  const apiFetch = useApi()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)
  const [upgraded, setUpgraded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (hasAccess) return <>{children}</>

  const label = requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)

  async function handleUpgrade() {
    setLoading(true)
    setError(null)
    const res = await apiFetch<CheckoutResponse>('/api/billing/checkout', {
      method: 'POST',
      body: { plan: requiredPlan },
    })
    if (res.success && res.data?.upgraded) {
      // Existing subscriber — plan changed in place, no Stripe redirect.
      // usePlan() reads the cached /auth/me row, so refetch it and reload
      // to unlock every gate on the page.
      setUpgraded(true)
      setLoading(false)
      await queryClient.invalidateQueries({ queryKey: ['user-me'] })
      setTimeout(() => window.location.reload(), 1200)
      return
    }
    setLoading(false)
    if (res.success && res.data?.url) {
      window.location.href = res.data.url
      return
    }
    setError(res.error ?? 'Could not start the upgrade. Please try again.')
  }

  const upgradedNotice = upgraded ? (
    <p className="flex items-center gap-1.5 text-sm text-emerald-500">
      <CheckCircle2 className="h-4 w-4" /> Upgraded to {label}! Refreshing…
    </p>
  ) : null
  const errorNotice = error ? <p className="text-sm text-destructive">{error}</p> : null

  if (replace) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
        <Lock className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">This feature requires the {label} plan.</p>
        {upgradedNotice}
        {errorNotice}
        <button
          type="button"
          onClick={() => void handleUpgrade()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-full border border-cobalt-bright bg-primary/10 px-4 py-2 text-sm font-medium text-cobalt-bright cursor-pointer transition-colors hover:bg-primary hover:text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Lock className="h-3.5 w-3.5" />}
          {loading ? 'Redirecting...' : `Upgrade to ${label}`}
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      <div className="opacity-50 pointer-events-none select-none">{children}</div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
        {upgradedNotice}
        {errorNotice}
        <button
          type="button"
          onClick={() => void handleUpgrade()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-full border border-input bg-background/90 px-3 py-1.5 text-sm shadow-sm cursor-pointer transition-colors hover:bg-primary hover:text-primary-foreground hover:border-cobalt-bright disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Lock className="h-3.5 w-3.5" />}
          {loading ? 'Redirecting...' : `Upgrade to ${label}`}
        </button>
      </div>
    </div>
  )
}
